import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { leadAPI, taskAPI, Lead, LeadActivity, CRMTask, CRMUser, crmUserAPI } from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

const LeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [users, setUsers] = useState<CRMUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Activity Modal
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activity_type: 'NOTE',
    subject: '',
    notes: '',
    outcome: '',
    follow_up_needed: false,
    follow_up_date: '',
  });

  // Task Modal
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'MEDIUM',
    assigned_to: '',
  });

  // Convert Modal
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertForm, setConvertForm] = useState({
    name: '',
    customer_type: 'WHOLESALE',
  });

  useEffect(() => {
    loadLead();
    loadUsers();
  }, [id]);

  const loadLead = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await leadAPI.getById(parseInt(id));
      setLead(response.data);
      setConvertForm({
        name: response.data.business_name,
        customer_type: 'WHOLESALE',
      });
    } catch (err: any) {
      setError('Error loading lead');
      console.error('Error loading lead:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await crmUserAPI.getAll();
      setUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleAddActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await leadAPI.addActivity(parseInt(id), activityForm);
      setShowActivityModal(false);
      setActivityForm({
        activity_type: 'NOTE',
        subject: '',
        notes: '',
        outcome: '',
        follow_up_needed: false,
        follow_up_date: '',
      });
      loadLead();
    } catch (err) {
      console.error('Error adding activity:', err);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await taskAPI.create({
        ...taskForm,
        lead_id: parseInt(id),
        assigned_to: taskForm.assigned_to ? parseInt(taskForm.assigned_to) : undefined,
      });
      setShowTaskModal(false);
      setTaskForm({
        title: '',
        description: '',
        due_date: '',
        priority: 'MEDIUM',
        assigned_to: '',
      });
      loadLead();
    } catch (err) {
      console.error('Error adding task:', err);
    }
  };

  const handleCompleteTask = async (taskId: number) => {
    try {
      await taskAPI.complete(taskId);
      loadLead();
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  const handleConvertToCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await leadAPI.convertToCustomer(parseInt(id), convertForm);
      setShowConvertModal(false);
      loadLead();
    } catch (err) {
      console.error('Error converting to customer:', err);
    }
  };

  const handleDeleteLead = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      return;
    }
    try {
      await leadAPI.delete(parseInt(id));
      navigate('/crm/leads');
    } catch (err) {
      console.error('Error deleting lead:', err);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'CALL': return '📞';
      case 'EMAIL': return '📧';
      case 'VISIT': return '🚗';
      case 'MEETING': return '👥';
      case 'NOTE': return '📝';
      case 'SAMPLE_SENT': return '📦';
      case 'QUOTE_SENT': return '💰';
      default: return '📌';
    }
  };

  const getStatusClass = (status: string) => {
    return `status-badge ${status.toLowerCase().replace('_', '-')}`;
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="crm-page">
        <CRMSubNav />
        <div className="loading">Loading lead...</div>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="crm-page">
        <CRMSubNav />
        <div className="error-state">
          <h3>Lead not found</h3>
          <Link to="/crm/leads" className="btn btn-primary">Back to Leads</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="crm-page">
      <CRMSubNav />

      <div className="crm-header">
        <div>
          <Link to="/crm/leads" className="back-link">← Back to Leads</Link>
          <h1>{lead.business_name}</h1>
          <div className="lead-meta">
            <span className={getStatusClass(lead.status)}>{lead.status_display}</span>
            <span className="meta-separator">•</span>
            <span>{lead.business_type_display}</span>
            <span className="meta-separator">•</span>
            <span>{lead.source_display}</span>
          </div>
        </div>
        <div className="crm-header-actions">
          {!lead.converted_to_customer && (
            <button
              className="btn btn-success"
              onClick={() => setShowConvertModal(true)}
            >
              Convert to Customer
            </button>
          )}
          <Link to={`/crm/leads/${id}/edit`} className="btn btn-primary">
            Edit Lead
          </Link>
          <button className="btn btn-danger" onClick={handleDeleteLead}>
            Delete
          </button>
        </div>
      </div>

      {lead.converted_to_customer && (
        <div className="converted-banner">
          ✅ This lead was converted to customer: <strong>{lead.customer_name}</strong> on {formatDate(lead.converted_date)}
        </div>
      )}

      <div className="lead-detail-grid">
        {/* Left Column - Lead Info */}
        <div className="lead-info-section">
          {/* Business Info */}
          <div className="info-card">
            <h3>Business Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Business Name</label>
                <span>{lead.business_name}</span>
              </div>
              <div className="info-item">
                <label>Business Type</label>
                <span>{lead.business_type_display}</span>
              </div>
              {lead.website && (
                <div className="info-item">
                  <label>Website</label>
                  <a href={lead.website} target="_blank" rel="noopener noreferrer">{lead.website}</a>
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="info-card">
            <h3>Location</h3>
            <div className="info-grid">
              <div className="info-item full-width">
                <label>Address</label>
                <span>{lead.address}</span>
              </div>
              <div className="info-item">
                <label>City</label>
                <span>{lead.city}</span>
              </div>
              <div className="info-item">
                <label>State</label>
                <span>{lead.state}</span>
              </div>
              <div className="info-item">
                <label>ZIP Code</label>
                <span>{lead.zip_code}</span>
              </div>
              {lead.region && (
                <div className="info-item">
                  <label>Region</label>
                  <span>{lead.region}</span>
                </div>
              )}
              {lead.distance_from_kettle_falls && (
                <div className="info-item">
                  <label>Distance from Kettle Falls</label>
                  <span>{lead.distance_from_kettle_falls} miles</span>
                </div>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="info-card">
            <h3>Contact Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Contact Name</label>
                <span>{lead.contact_name}</span>
              </div>
              {lead.contact_title && (
                <div className="info-item">
                  <label>Title</label>
                  <span>{lead.contact_title}</span>
                </div>
              )}
              <div className="info-item">
                <label>Phone</label>
                <a href={`tel:${lead.phone}`}>{lead.phone}</a>
              </div>
              {lead.email && (
                <div className="info-item">
                  <label>Email</label>
                  <a href={`mailto:${lead.email}`}>{lead.email}</a>
                </div>
              )}
              <div className="info-item">
                <label>Preferred Contact</label>
                <span>{lead.preferred_contact_method_display}</span>
              </div>
            </div>
          </div>

          {/* Sales Info */}
          <div className="info-card">
            <h3>Sales Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Assigned To</label>
                <span>{lead.assigned_to || 'Unassigned'}</span>
              </div>
              {lead.estimated_monthly_volume && (
                <div className="info-item">
                  <label>Est. Monthly Volume</label>
                  <span>{lead.estimated_monthly_volume}</span>
                </div>
              )}
              {lead.interested_products && (
                <div className="info-item full-width">
                  <label>Interested Products</label>
                  <span>{lead.interested_products}</span>
                </div>
              )}
              <div className="info-item">
                <label>Last Contact</label>
                <span>{formatDate(lead.last_contact_date)}</span>
              </div>
              <div className="info-item">
                <label>Next Follow-up</label>
                <span className={lead.is_follow_up_overdue ? 'text-danger' : ''}>
                  {formatDate(lead.next_follow_up)}
                  {lead.is_follow_up_overdue && ' (Overdue)'}
                </span>
              </div>
            </div>
            {lead.notes && (
              <div className="notes-section">
                <label>Notes</label>
                <p>{lead.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Activity & Tasks */}
        <div className="lead-activity-section">
          {/* Quick Actions */}
          <div className="quick-actions">
            <button className="btn btn-primary" onClick={() => setShowActivityModal(true)}>
              + Log Activity
            </button>
            <button className="btn btn-outline" onClick={() => setShowTaskModal(true)}>
              + Add Task
            </button>
          </div>

          {/* Tasks */}
          <div className="activity-card">
            <h3>Tasks</h3>
            {(!lead.tasks || lead.tasks.length === 0) ? (
              <div className="empty-state small">
                <p>No tasks</p>
              </div>
            ) : (
              <ul className="task-list">
                {lead.tasks.filter(t => !t.completed).map(task => (
                  <li key={task.id} className={`task-item ${task.is_overdue ? 'overdue' : ''}`}>
                    <input
                      type="checkbox"
                      className="task-checkbox"
                      onChange={() => handleCompleteTask(task.id)}
                    />
                    <div className="task-content">
                      <div className="task-title">{task.title}</div>
                      {task.description && (
                        <div className="task-description">{task.description}</div>
                      )}
                    </div>
                    <div className="task-meta">
                      <span className={`task-priority ${task.priority.toLowerCase()}`}>
                        {task.priority}
                      </span>
                      <span className={task.is_overdue ? 'text-danger' : ''}>
                        {formatDate(task.due_date)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="activity-card">
            <h3>Activity Timeline</h3>
            {(!lead.activities || lead.activities.length === 0) ? (
              <div className="empty-state small">
                <p>No activity logged yet</p>
              </div>
            ) : (
              <ul className="activity-timeline">
                {lead.activities.map(activity => (
                  <li key={activity.id} className="timeline-item">
                    <div className="timeline-icon">
                      {getActivityIcon(activity.activity_type)}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <strong>{activity.subject}</strong>
                        <span className="timeline-type">{activity.activity_type_display}</span>
                      </div>
                      {activity.notes && (
                        <p className="timeline-notes">{activity.notes}</p>
                      )}
                      {activity.outcome && (
                        <p className="timeline-outcome">
                          <strong>Outcome:</strong> {activity.outcome}
                        </p>
                      )}
                      <div className="timeline-meta">
                        {activity.created_by} • {formatDateTime(activity.created_at)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Activity Modal */}
      {showActivityModal && (
        <div className="modal-overlay" onClick={() => setShowActivityModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Log Activity</h2>
              <button className="modal-close" onClick={() => setShowActivityModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddActivity}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Activity Type</label>
                  <select
                    value={activityForm.activity_type}
                    onChange={e => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                    required
                  >
                    <option value="CALL">Phone Call</option>
                    <option value="EMAIL">Email</option>
                    <option value="VISIT">Site Visit</option>
                    <option value="MEETING">Meeting</option>
                    <option value="NOTE">Note</option>
                    <option value="SAMPLE_SENT">Sent Samples</option>
                    <option value="QUOTE_SENT">Sent Quote</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Subject</label>
                  <input
                    type="text"
                    value={activityForm.subject}
                    onChange={e => setActivityForm({ ...activityForm, subject: e.target.value })}
                    placeholder="Brief description of the activity"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={activityForm.notes}
                    onChange={e => setActivityForm({ ...activityForm, notes: e.target.value })}
                    placeholder="Detailed notes..."
                    rows={4}
                  />
                </div>
                <div className="form-group">
                  <label>Outcome</label>
                  <input
                    type="text"
                    value={activityForm.outcome}
                    onChange={e => setActivityForm({ ...activityForm, outcome: e.target.value })}
                    placeholder="Result of the activity"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={activityForm.follow_up_needed}
                      onChange={e => setActivityForm({ ...activityForm, follow_up_needed: e.target.checked })}
                    />
                    Schedule follow-up
                  </label>
                </div>
                {activityForm.follow_up_needed && (
                  <div className="form-group">
                    <label>Follow-up Date</label>
                    <input
                      type="date"
                      value={activityForm.follow_up_date}
                      onChange={e => setActivityForm({ ...activityForm, follow_up_date: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowActivityModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Log Activity
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Task</h2>
              <button className="modal-close" onClick={() => setShowTaskModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddTask}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={taskForm.title}
                    onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                    placeholder="Task title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={taskForm.description}
                    onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                    placeholder="Task details..."
                    rows={3}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={taskForm.due_date}
                      onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select
                      value={taskForm.priority}
                      onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Assign To</label>
                  <select
                    value={taskForm.assigned_to}
                    onChange={e => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                  >
                    <option value="">Select user...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.full_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowTaskModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Convert to Customer Modal */}
      {showConvertModal && (
        <div className="modal-overlay" onClick={() => setShowConvertModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Convert to Customer</h2>
              <button className="modal-close" onClick={() => setShowConvertModal(false)}>×</button>
            </div>
            <form onSubmit={handleConvertToCustomer}>
              <div className="modal-body">
                <p className="modal-info">
                  This will create a new customer record based on this lead's information.
                </p>
                <div className="form-group">
                  <label>Customer Name</label>
                  <input
                    type="text"
                    value={convertForm.name}
                    onChange={e => setConvertForm({ ...convertForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Customer Type</label>
                  <select
                    value={convertForm.customer_type}
                    onChange={e => setConvertForm({ ...convertForm, customer_type: e.target.value })}
                  >
                    <option value="WHOLESALE">Wholesale</option>
                    <option value="RETAIL">Retail</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowConvertModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success">
                  Convert to Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetail;
